import React from 'react';

export const formatInlineMarkdown = (text: string): React.ReactNode => {
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return React.createElement('strong', { key: i, className: 'font-semibold' }, part.slice(2, -2));
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**'))
      return React.createElement('em', { key: i, className: 'italic' }, part.slice(1, -1));
    if (part.startsWith('`') && part.endsWith('`'))
      return React.createElement('code', { key: i, className: 'bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono' }, part.slice(1, -1));
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch)
      return React.createElement('a', { key: i, href: linkMatch[2], target: '_blank', rel: 'noopener noreferrer', className: 'text-np-600 hover:text-np-700 underline' }, linkMatch[1]);
    return part;
  });
};

const MAX_RECURSION_DEPTH = 3;

export const formatMarkdown = (text: string, depth: number = 0): React.ReactNode[] => {
  // section/blockquote 내부 재귀가 무한히 깊어지지 않도록 cap
  if (depth >= MAX_RECURSION_DEPTH) {
    return [React.createElement('span', { key: 'depth-cap' }, text)];
  }
  const codeBlockRegex = /(```[\s\S]*?```)/g;
  const parts = text.split(codeBlockRegex);
  const allNodes: React.ReactNode[] = [];

  parts.forEach((part, partIdx) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      const codeContent = part.slice(3, -3);
      const langMatch = codeContent.match(/^(\w+)\n/);
      const lang = langMatch ? langMatch[1] : '';
      const code = langMatch ? codeContent.slice(langMatch[0].length) : codeContent;
      allNodes.push(
        React.createElement('pre', { key: `code-${partIdx}`, className: 'bg-gray-800 text-gray-100 p-3 rounded-md text-xs overflow-x-auto my-2 font-mono' },
          lang && React.createElement('div', { className: 'text-gray-400 text-[10px] mb-1' }, lang),
          React.createElement('code', null, code)
        )
      );
      return;
    }

    const lines = part.split('\n');
    const result: React.ReactNode[] = [];
    let listItems: React.ReactNode[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let tableRows: string[] = [];
    let sectionLines: string[] | null = null;
    let blockquoteLines: string[] | null = null;

    const flushList = () => {
      if (listItems.length > 0) {
        const listEl = listType === 'ol'
          ? React.createElement('ol', { key: `ol-${result.length}`, className: 'list-decimal list-inside my-1.5 space-y-0.5 pl-1' }, ...listItems)
          : React.createElement('ul', { key: `ul-${result.length}`, className: 'list-disc list-inside my-1.5 space-y-0.5 pl-1' }, ...listItems);
        result.push(listEl);
        listItems = [];
        listType = null;
      }
    };

    const flushSection = () => {
      if (sectionLines !== null && sectionLines.length > 0) {
        // 섹션 내부를 재귀 파싱하여 표/리스트/헤딩 등 block-level 마크다운 지원
        const innerText = sectionLines.join('\n');
        result.push(
          React.createElement(
            'div',
            { key: `section-${result.length}`, className: 'my-2 pl-3 py-2 border-l-2 border-np-300 bg-np-50/50 rounded-r-md text-sm leading-relaxed' },
            ...formatMarkdown(innerText, depth + 1)
          )
        );
      }
      sectionLines = null;
    };

    const flushBlockquote = () => {
      if (blockquoteLines !== null && blockquoteLines.length > 0) {
        const innerText = blockquoteLines.join('\n');
        result.push(
          React.createElement(
            'div',
            { key: `bq-${result.length}`, className: 'pl-3 border-l-2 border-gray-300 text-gray-600 italic my-2' },
            ...formatMarkdown(innerText, depth + 1)
          )
        );
      }
      blockquoteLines = null;
    };

    const flushTable = () => {
      if (tableRows.length < 1 || !tableRows[0].includes('|')) {
        tableRows.forEach(row => result.push(React.createElement('span', { key: `tr-${result.length}` }, row, React.createElement('br'))));
        tableRows = [];
        return;
      }
      const parseCells = (row: string) => row.split('|').map(c => c.trim()).filter(c => c !== '');
      const isSeparator = (row: string) => /^\|?\s*[-:]+[-|\s:]*$/.test(row.trim());
      const headers = parseCells(tableRows[0]);
      const bodyRows = tableRows.slice(1).filter(r => !isSeparator(r));

      const tblKey = `tbl-${result.length}`;
      const isKeyValue = headers.length === 2 && bodyRows.length >= 2;

      if (isKeyValue) {
        const rows = bodyRows.map(r => parseCells(r));
        const fieldNames = rows.map(r => r[0] || '');
        const fieldValues = rows.map(r => r[1] || '');
        result.push(
          React.createElement('div', { key: tblKey, className: 'my-2 overflow-x-auto' },
            React.createElement('table', { className: 'min-w-full text-xs border border-gray-300 rounded' },
              React.createElement('thead', { className: 'bg-gray-100' },
                React.createElement('tr', null,
                  ...fieldNames.map((fn, i) => React.createElement('th', { key: i, className: 'px-3 py-1.5 text-left font-semibold border-b border-gray-300 whitespace-nowrap' }, fn))
                )
              ),
              React.createElement('tbody', null,
                React.createElement('tr', { className: 'border-b border-gray-200 hover:bg-gray-50' },
                  ...fieldValues.map((fv, i) => React.createElement('td', { key: i, className: 'px-3 py-1.5' }, fv))
                )
              )
            )
          )
        );
      } else {
        result.push(
          React.createElement('div', { key: tblKey, className: 'my-2 overflow-x-auto' },
            React.createElement('table', { className: 'min-w-full text-xs border border-gray-300 rounded' },
              React.createElement('thead', { className: 'bg-gray-100' },
                React.createElement('tr', null,
                  ...headers.map((h, i) => React.createElement('th', { key: i, className: 'px-3 py-1.5 text-left font-semibold border-b border-gray-300' }, h))
                )
              ),
              React.createElement('tbody', null,
                ...bodyRows.map((row, ri) => {
                  const cells = parseCells(row);
                  return React.createElement('tr', { key: ri, className: 'border-b border-gray-200 hover:bg-gray-50' },
                    ...cells.map((c, ci) => React.createElement('td', { key: ci, className: 'px-3 py-1.5' }, c))
                  );
                })
              )
            )
          )
        );
      }
      tableRows = [];
    };

    lines.forEach((line, li) => {
      const key = `${partIdx}-${li}`;
      const trimmed = line.trim();

      // --- separator: toggle section collection
      if (/^-{3,}$/.test(trimmed)) {
        flushList();
        flushBlockquote();
        if (tableRows.length > 0) flushTable();
        if (sectionLines !== null) {
          flushSection();
        } else {
          sectionLines = [];
        }
        return;
      }

      if (sectionLines !== null) {
        sectionLines.push(line);
        return;
      }

      // Blockquote 라인 — 연속된 > 라인을 그룹화하여 내부를 재귀 파싱
      const bqMatch = line.match(/^>\s?(.*)$/);
      if (bqMatch) {
        flushList();
        if (tableRows.length > 0) flushTable();
        if (blockquoteLines === null) blockquoteLines = [];
        blockquoteLines.push(bqMatch[1] || '');
        return;
      } else if (blockquoteLines !== null) {
        flushBlockquote();
      }

      const isTableRow = trimmed.startsWith('|') || (tableRows.length > 0 && trimmed.includes('|') && !trimmed.startsWith('#'));

      if (isTableRow) {
        flushList();
        tableRows.push(line);
        return;
      } else if (tableRows.length > 0) {
        flushTable();
      }

      // Headings — rendered as section dividers with top border
      const h = line.match(/^(#{1,6})\s+(.+)$/);
      if (h) {
        flushList();
        const level = h[1].length;
        const isTopLevel = level <= 2;
        const tag = level <= 2 ? 'h2' : level === 3 ? 'h3' : level === 4 ? 'h4' : 'h5';
        const cls = isTopLevel
          ? 'font-semibold text-sm text-gray-900 mt-4 mb-1.5 pt-3 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0'
          : level === 3
          ? 'font-semibold text-sm text-gray-800 mt-3 mb-1'
          : 'font-medium text-sm mt-2 mb-0.5 text-gray-600';
        result.push(React.createElement(tag, { key, className: cls }, formatInlineMarkdown(h[2])));
        return;
      }

      const ul = line.match(/^[\-\*]\s+(.+)$/);
      if (ul) { if (listType !== 'ul') { flushList(); listType = 'ul'; } listItems.push(React.createElement('li', { key }, formatInlineMarkdown(ul[1]))); return; }

      const ol = line.match(/^\d+\.\s+(.+)$/);
      if (ol) { if (listType !== 'ol') { flushList(); listType = 'ol'; } listItems.push(React.createElement('li', { key }, formatInlineMarkdown(ol[1]))); return; }

      flushList();
      if (line.trim() === '') { result.push(React.createElement('br', { key })); }
      else { result.push(React.createElement('span', { key }, formatInlineMarkdown(line), li < lines.length - 1 ? React.createElement('br') : null)); }
    });
    flushList();
    flushTable();
    flushSection();
    flushBlockquote();
    allNodes.push(...result);
  });
  return allNodes;
};
